import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Text
} from "@chakra-ui/react"
import Footer from "../components/Footer"

const steps = [
  {
    label: "Identify",
    description:
      "We identify high retrofit potential homes that allow you to empower your customers to adopt low carbon technologies with strong ROI."
  },
  {
    label: "Analyse",
    description:
      "Our AI model helps you spot opportunities based on strong alignment with your tariffs and service offerings."
  },
  {
    label: "Unlock",
    description:
      "We monitor portfolio performance and report impact, helping you to track the outcomes of your interventions and meet your regulatory requirements with confidence."
  }
]

const benefits = [
  {
    title: "Enable Data-Driven Customer Decisions",
    body: "Give customers clear, personalised insight into energy upgrades: costs, savings, and ROI, removing uncertainty and accelerating adoption."
  },
  {
    title: "Meet Regulatory Requirements",
    body: "Deliver against obligations with confidence. Target the right homes and track impact with precision."
  },
  {
    title: "Drive Low-Carbon Technology Uptake",
    body: "Increase deployment of heat pumps, solar, and battery systems. Turn insight into action and scale your installations efficiently."
  },
  {
    title: "Unlock New Revenue Streams",
    body: "Support innovative tariffs, flexibility services, and smart energy usage. Monetise generation, storage, and demand-side response."
  },
  {
    title: "Reduce Costs and Improve Accuracy",
    body: "Replace manual surveys with scalable, data-driven analysis. Improve accuracy of retrofit recommendations."
  },
  {
    title: "Lead the Net Zero Transition",
    body: "Position your brand as a credible, climate-led energy provider. Win and retain customers who value sustainability."
  },
  {
    title: "Increase Customer Engagement & Retention",
    body: "Help customers understand how they benefit from low carbon technology. Build trust through transparency and better outcomes."
  },
  {
    title: "High Quality Lead Generation",
    body: "We connect you with pre-qualified warm leads for customers who are actively looking for renewable upgrades and understand their potential returns. "
  }
]

const EnergyUtilities: React.FC = () => (
  <Box>
    {/* Hero */}
    <Box bg="brand.700">
      <Container maxW="container.md" py={24} textAlign="center">
        <Heading as="h1" size="5xl" color="white" mb={6}>
          Energy Companies
        </Heading>
        <Text fontSize="xl" color="whiteAlpha.900" maxW={700} m="auto">
          GreenFlip enables energy companies to drive customer adoption of
          low-carbon technologies, while meeting regulatory targets and
          unlocking new revenue streams.
        </Text>
      </Container>
    </Box>

    {/* How it works */}
    <Container maxW="container.md" py={20}>
      <HStack gap={6} align="stretch" flexWrap={["wrap", "nowrap"]}>
        {steps.map((step, i) => (
          <Box
            key={step.label}
            flex={1}
            minW={["100%", "0"]}
            bg="gray.50"
            p={8}
            borderRadius="2xl"
            borderTop="4px solid"
            borderColor="brand.600"
          >
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="brand.600"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={2}
            >
              {String(i + 1).padStart(2, "0")}
            </Text>
            <Heading as="h3" size="lg" mb={3} color="green.800">
              {step.label}
            </Heading>
            <Text color="gray.600">{step.description}</Text>
          </Box>
        ))}
      </HStack>
    </Container>

    {/* Benefits */}
    <Box bg="gray.50">
      <Container maxW="container.md" py={20}>
        <Heading
          as="h2"
          size="3xl"
          mb={12}
          textAlign="center"
          color="green.800"
        >
          Key Benefits for Energy Companies
        </Heading>
        <SimpleGrid columns={[1, 2]} gap={6}>
          {benefits.map((b) => (
            <Box key={b.title} bg="white" p={6} borderRadius="xl" shadow="sm">
              <Heading as="h3" size="md" mb={3} color="green.800">
                {b.title}
              </Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                {b.body}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>

    {/* CTA */}
    <Box bg="brand.700">
      <Container maxW="container.md" py={20} textAlign="center">
        <Heading as="h2" size="3xl" color="white" mb={6}>
          Scale your customers' low-carbon technology uptake
        </Heading>
        <Text color="whiteAlpha.900" fontSize="lg" maxW={600} m="auto" mb={8}>
          Explore how we can enable smarter energy decisions and help your
          customers make the switch with confidence.
        </Text>
        <Link href="/contact">
          <Button
            bg="white"
            color="brand.700"
            size="lg"
            _hover={{ bg: "gray.100" }}
          >
            Get in touch
          </Button>
        </Link>
      </Container>
    </Box>

    <Footer />
  </Box>
)

export default EnergyUtilities
